import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Table2, Eye, EyeOff, Trash2 } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TablesPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const fetchTables = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/campaigns/${campaignId}/tables`, { withCredentials: true });
      setTables(res.data || []);
    } catch { toast.error("Failed to load tables"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTables(); }, [campaignId]);

  const createTable = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Name is required"); return; }
    try {
      const res = await axios.post(`${API}/tables`, {
        campaign_id: campaignId,
        name,
        columns: ["Dice", "Effect"],
        rows: [["", ""]],
      }, { withCredentials: true });
      setTables((p) => [...p, res.data]);
      setCreating(false);
      setNewName("");
      navigate(`/campaign/${campaignId}/tables/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create table");
    }
  };

  const removeTable = async (t) => {
    if (!await confirm("Delete table?", `"${t.name}" and all its rows will be permanently removed.`)) return;
    try {
      await axios.delete(`${API}/tables/${t.id}`, { withCredentials: true });
      setTables((p) => p.filter((x) => x.id !== t.id));
      toast.success("Table deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <Layout>
      <div className="w-full" data-testid="tables-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-serif text-foreground flex items-center gap-2">
              <Table2 className="w-7 h-7 text-primary" /> Tables
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Wild magic surge tables, calendars, magic systems, weapon lists &mdash; any custom table you need.
              Paste from Excel or upload a spreadsheet to auto-fill.
            </p>
          </div>
          <Button
            onClick={() => setCreating(true)}
            className="bg-primary text-primary-foreground rounded-sm"
            data-testid="new-table-button"
          >
            <Plus className="w-4 h-4 mr-1" /> New Table
          </Button>
        </div>

        {loading ? (
          <p className="text-muted-foreground text-sm">Loading&hellip;</p>
        ) : tables.length === 0 ? (
          <Card className="bg-card border-border" data-testid="tables-empty">
            <CardContent className="p-8 text-center">
              <Table2 className="w-10 h-10 text-muted-foreground/50 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                No tables yet. Click <span className="text-foreground">New Table</span> to create your first one &mdash;
                perfect for wild magic surges, calendars, and rarity charts.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {tables.map((t) => (
              <Card
                key={t.id}
                className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer group"
                onClick={() => navigate(`/campaign/${campaignId}/tables/${t.id}`)}
                data-testid={`table-card-${t.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="font-serif text-lg text-foreground group-hover:text-primary transition-colors leading-tight">{t.name}</h3>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeTable(t); }}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-400 transition-opacity"
                      data-testid={`delete-table-${t.id}`}
                      title="Delete table"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {t.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{t.description}</p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{(t.columns || []).length} cols &middot; {(t.rows || []).length} rows</span>
                    <span className="flex items-center gap-1">
                      {t.visible_to_players ? (
                        <><Eye className="w-3 h-3 text-primary" /><span className="text-primary">Shared</span></>
                      ) : (
                        <><EyeOff className="w-3 h-3" />DM only</>
                      )}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={creating} onOpenChange={setCreating}>
          <DialogContent data-testid="new-table-dialog">
            <DialogHeader>
              <DialogTitle>New Table</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Zerhadun Wild Magic Surge Table"
                onKeyDown={(e) => { if (e.key === "Enter") createTable(); }}
                data-testid="new-table-name-input"
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
                <Button onClick={createTable} className="bg-primary text-primary-foreground" data-testid="new-table-create-button">
                  Create
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TablesPage;
